// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React from 'react';
import { Typography, Tooltip, IconButton, useTheme } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { EnsureGeoidOffsetReason } from '../../elevationThunk';
import { DisplayUnit, toDisplayValue } from '../../cadUnits';
import stylesDef from './styles';

// 'pending' = nothing placed yet: a draft's correction is shown but not applied.
export type GeoidStatus = 'resolved' | 'resolving' | 'unavailable' | 'pending';

// One explanation of what the correction is, shared by every state that describes it, so the
// pending and applied tooltips read as the same story told before and after placement.
const GEOID_EXPLANATION = 'It is the gap between the correct geoid your data uses and Esri’s ground surface (WGS84 ellipsoid + terrain). This adjustment accounts for undulation and ensures your data sits correctly on the ground. It never changes your data.';
const NOTHING_APPLIED_YET = 'Nothing is applied yet: your boundary is drawn on the ground as-is.';

const magnitude = (valueMetres: number, unit: DisplayUnit, feetPerMetre: number) =>
  `${Math.abs(toDisplayValue(valueMetres, unit, feetPerMetre)).toFixed(2)} ${unit}`;

const signed = (valueMetres: number, unit: DisplayUnit, feetPerMetre: number) => {
  if (valueMetres > 0) return `+${magnitude(valueMetres, unit, feetPerMetre)}`;
  // A proper minus sign (−, U+2212), not the hyphen-minus Number#toFixed would produce for a
  // negative value.
  if (valueMetres < 0) return `−${magnitude(valueMetres, unit, feetPerMetre)}`;
  return magnitude(valueMetres, unit, feetPerMetre);
};

interface ElevationOffsetIconProps {
  // Whether a correction is in force, still being computed, cannot be determined, or is waiting on
  // placement. Picks which explanation below to show.
  geoidStatus: GeoidStatus;
  // The order's automatic geoid correction in metres; only read when geoidStatus is 'resolved'.
  projectGeoidOffset: number;
  // The draft order's stored correction in metres, or null; only read when geoidStatus is 'pending'.
  pendingGeoidOffset: number | null;
  feetPerMetre: number;
  displayUnit: DisplayUnit;
}

/**
 * The 'i' explainer for the elevation-offset box, next to its "Elevation Offset" label. States the
 * actual geoid-correction number, so a reader gets the specific value without a separate line for it.
 *
 * A hover Tooltip rather than a click popover: it opens and closes itself as the pointer moves
 * over and off the icon, with no state of its own to manage or click-away to dismiss.
 */
export const ElevationOffsetIcon = ({
  geoidStatus,
  projectGeoidOffset,
  pendingGeoidOffset,
  feetPerMetre,
  displayUnit,
}: ElevationOffsetIconProps) => {
  const theme = useTheme();
  const styles = stylesDef(theme);

  const explanation = (() => {
    if (geoidStatus === 'unavailable') {
      return 'No automatic geoid correction was found for this order. This is a visualization adjustment we normally make to Esri’s ground surface (WGS84 ellipsoid + terrain) so your data sits flush on the 3D terrain. Your data itself is unchanged. Use the field above to adjust the ground surface as needed to match your data.';
    }
    if (geoidStatus === 'resolving') {
      return 'Checking whether this order has an automatic geoid correction based on the KML location.';
    }
    if (geoidStatus === 'pending') {
      if (pendingGeoidOffset === null) {
        return `An automatic geoid correction will be computed for this order when a KML is uploaded. ${GEOID_EXPLANATION} It takes effect once your order is placed.`;
      }
      return `An automatic geoid correction of ${signed(pendingGeoidOffset, displayUnit, feetPerMetre)} is ready for this order and will be applied for visualization once your order is placed. ${GEOID_EXPLANATION} ${NOTHING_APPLIED_YET}`;
    }
    return `An automatic geoid correction of ${signed(projectGeoidOffset, displayUnit, feetPerMetre)} has been applied for visualization. ${GEOID_EXPLANATION} Use the field above to fine tune the ground surface further if needed.`;
  })();

  return (
    <Tooltip
      title={explanation}
      placement="top"
      arrow
      enterTouchDelay={0}
      PopperProps={{
        sx: styles.tooltip,
        // Without this, flip/overflow checks are computed against the sidebar's own scrollable
        // container (the nearest clipping ancestor) — but the tooltip itself is portaled outside
        // it, so that boundary doesn't match where it actually renders. Pointing both at the real
        // viewport is what lets it flip to the bottom when there's no room above.
        modifiers: [
          {
            name: 'flip',
            enabled: true,
            options: { fallbackPlacements: ['bottom', 'right', 'left'], rootBoundary: 'viewport' },
          },
          {
            name: 'preventOverflow',
            enabled: true,
            options: { boundary: 'viewport', altAxis: true, padding: 8 },
          },
        ],
      }}
    >
      <IconButton size="small" sx={styles.infoIconButton} aria-label="About the elevation offset">
        <InfoOutlinedIcon sx={styles.infoIcon} />
      </IconButton>
    </Tooltip>
  );
};

interface ElevationOffsetInfoProps {
  // Whether a correction is in force, still being computed, cannot be determined, or is waiting
  // on placement.
  geoidStatus: GeoidStatus;
  // Why geoidStatus is 'unavailable' — distinguishes "this order has none" (expected) from a
  // genuine failure worth retrying. Ignored unless geoidStatus is 'unavailable'.
  geoidUnavailableReason: EnsureGeoidOffsetReason | null;
  // Whether the draft already holds a correction; only read when geoidStatus is 'pending'.
  pendingGeoidOffset: number | null;
  // Whether the user's most recent edit failed to reach the server — the terrain still reflects it
  // for this session, but it will not survive a reload unless they try saving again.
  elevationSaveFailed: boolean;
}

/**
 * A one-line status for the elevation-offset input above: whether a correction is in force, still
 * being checked, or missing — full detail lives behind the 'i' icon in the box header instead of
 * repeating numbers this component doesn't need to know.
 */
export const ElevationOffsetInfo = ({
  geoidStatus,
  geoidUnavailableReason,
  pendingGeoidOffset,
  elevationSaveFailed,
}: ElevationOffsetInfoProps) => {
  const theme = useTheme();
  const styles = stylesDef(theme);

  const message = (): string => {
    if (geoidStatus === 'resolving') return 'Checking for a geoid correction…';
    if (geoidStatus === 'resolved') return 'Geoid correction applied. Adjust above if this still looks off.';
    if (geoidStatus === 'pending') {
      return pendingGeoidOffset === null
        ? 'Geoid correction is set up when a KML is uploaded.'
        : 'Geoid correction ready. It applies once your order is placed.';
    }
    // 'no-centroid' (or no reason at all, defensively) is the expected case — say so plainly. The
    // other two are transient failures, not a property of the order, so say that instead of
    // repeating the same message for a problem worth retrying.
    if (geoidUnavailableReason === 'lookup-failed') {
      return 'Couldn’t set a geoid correction right now. Reload to retry, or adjust above.';
    }
    if (geoidUnavailableReason === 'request-failed') {
      return 'Couldn’t check for a geoid correction. Reload to retry, or adjust above.';
    }
    return 'No geoid correction for this order. Adjust above if the terrain looks off.';
  };

  const messageStyle = () => {
    if (geoidStatus === 'resolving') return styles.noteResolving;
    if (geoidStatus === 'resolved' || geoidStatus === 'pending') return styles.note;
    return styles.noteMissing;
  };

  return (
    <>
      <Typography sx={messageStyle()}>{message()}</Typography>
      {elevationSaveFailed && (
        <Typography sx={styles.noteMissing}>
          Couldn’t save your elevation offset. It won’t persist past this session. Try again.
        </Typography>
      )}
    </>
  );
};

export default ElevationOffsetInfo;
